import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, fmtCompact } from "@/lib/api";
import SEO from "@/components/SEO";
import {
  METRO_STATIONS,
  TRAM_STATIONS,
  MONORAIL_STATIONS,
  ETIHAD_RAIL_STATIONS,
} from "@/lib/metroStations";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const PIN_COLORS = {
  project: "#001588",     // Deep Point Penta royal blue for Off-plan pins
  listing: "#F5C842",     // Warm gold for Live listings — pops on the dark map
  community: "#3B82F6",   // Bright sky-blue for Communities — same hue family as projects but clearly distinct
};

// Dubai / UAE transit network — every mode Point Penta considers "walkable"
// for a listing. Coords come from `metroStations.js` (single source of truth).
// All lines rendered dotted per brand direction; hover a stop for its name.
const metroByLine = (line) =>
  METRO_STATIONS.filter((s) => s.line === line).map((s) => ({ name: s.name, lat: s.lat, lng: s.lng }));

const TRANSIT_LINES = [
  {
    id: "red",
    label: "Metro Red Line",
    color: "#E30613",
    stations: metroByLine("red"),
  },
  {
    id: "green",
    label: "Metro Green Line",
    color: "#009845",
    stations: metroByLine("green"),
  },
  {
    id: "blue",
    label: "Metro Blue Line (opens 2029)",
    color: "#0099D8",
    stations: metroByLine("blue"),
    future: true,
  },
  {
    id: "tram",
    label: "Dubai Tram",
    color: "#7B1FA2",
    stations: TRAM_STATIONS,
  },
  {
    id: "monorail",
    label: "Palm Monorail",
    color: "#FF6D00",
    stations: MONORAIL_STATIONS,
  },
  {
    id: "etihad",
    label: "Etihad Rail (2026)",
    color: "#00695C",
    stations: ETIHAD_RAIL_STATIONS,
    future: true,
  },
];

function TransitLine({ line, show }) {
  const map = useMap();
  useEffect(() => {
    if (!show || line.stations.length < 2) return undefined;
    const coords = line.stations.map((s) => [s.lat, s.lng]);
    const polyline = L.polyline(coords, {
      color: line.color,
      weight: 3.5,
      opacity: 0.9,
      dashArray: "4,8",           // all lines dotted per brand spec
      lineCap: "round",
      lineJoin: "round",
    }).addTo(map);
    polyline.bindTooltip(line.label, { sticky: true, className: "pp-metro-tooltip" });

    // Render each station stop as a small circle with hover-only tooltip
    const stopMarkers = line.stations.map((s) =>
      L.circleMarker([s.lat, s.lng], {
        radius: 4.5,
        color: "#ffffff",
        weight: 1.5,
        fillColor: line.color,
        fillOpacity: 1,
        opacity: 1,
      })
        .bindTooltip(s.name, {
          direction: "top",
          offset: [0, -6],
          className: "pp-station-tooltip",
        })
        .addTo(map),
    );

    return () => {
      polyline.remove();
      stopMarkers.forEach((m) => m.remove());
    };
  }, [line, show, map]);
  return null;
}

function makePinIcon(color, label) {
  // Auto-contrast the pin label — dark text on light pins (gold, white),
  // white text on the deep royal-blue pin. Keeps the P / L legible on both.
  const dark = ["#001588"].includes(color.toUpperCase()) || false;
  const textColor = dark ? "#FFFFFF" : "#050d19";
  return L.divIcon({
    className: "pp-map-pin",
    html: `<span style="
      display:flex;align-items:center;justify-content:center;
      width:34px;height:34px;background:${color};color:${textColor};
      border:2px solid rgba(255,255,255,0.9);border-radius:50%;
      box-shadow:0 4px 14px rgba(0,0,0,.45), 0 0 0 4px rgba(11,14,20,0.55);
      font-size:12px;font-weight:700;letter-spacing:0.02em;">${label}</span>`,
    iconSize: [34, 34], iconAnchor: [17, 17],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.latitude, p.longitude]));
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

export default function InteractiveMap() {
  const [pins, setPins] = useState({ projects: [], listings: [], communities: [] });
  const [developers, setDevelopers] = useState([]);
  const [transit, setTransit] = useState({
    red: true, green: true, blue: true,
    tram: true, monorail: true, etihad: false,
  });
  const [f, setF] = useState({
    community: "", developer_id: "", status: "",
    price_min: "", price_max: "", property_type: "", bedrooms_min: "",
    include: { projects: true, listings: true, communities: true },
  });

  useEffect(() => { api.get("/developers").then((r) => setDevelopers(r.data.items || [])); }, []);

  useEffect(() => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => {
      if (k === "include") return;
      if (v !== "" && v != null) q.set(k, v);
    });
    q.set("include", Object.entries(f.include).filter(([, v]) => v).map(([k]) => k).join(",") || "projects");
    api.get(`/map/pins?${q.toString()}`).then((r) => setPins(r.data));
  }, [f]);

  const allPoints = useMemo(() => {
    const out = [];
    if (f.include.projects) out.push(...(pins.projects || []).map((p) => ({ ...p, kind: "project" })));
    if (f.include.listings) out.push(...(pins.listings || []).map((p) => ({ ...p, kind: "listing" })));
    if (f.include.communities) out.push(...(pins.communities || []).map((p) => ({ ...p, kind: "community" })));
    return out.filter((p) => p.latitude && p.longitude);
  }, [pins, f.include]);

  const communities = useMemo(() => {
    const set = new Set();
    (pins.communities || []).forEach((c) => c.name && set.add(c.name));
    (pins.projects || []).forEach((c) => c.community && set.add(c.community));
    return Array.from(set).sort();
  }, [pins]);

  const inputCls = "w-full border border-neutral-300 bg-white px-3 py-2 text-sm text-[#050d19] placeholder:text-neutral-400 focus:outline-none focus:border-[#001588]";

  const totalPins = allPoints.length;

  return (
    <div className="min-h-screen bg-white text-[#050d19]" data-testid="interactive-map-page">
      <SEO
        title="Interactive Dubai Property Map — Point Penta"
        description="Explore every Point Penta project, community and property on the interactive Dubai real estate map. Filter by community, developer, price and handover."
      />

      {/* Editorial hero header — dark, only within this section */}
      <div className="relative bg-[#050d19] text-white border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ backgroundImage: "url('https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=85')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050d19] via-[#050d19]/90 to-transparent"></div>
        <div className="relative max-w-[1400px] mx-auto px-6 py-14 lg:py-20">
          <div className="overline text-[#ffffff] mb-4">Point Penta · Live map</div>
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-3xl">
            Dubai on one map.<br/>
            <span>Every mandate, live.</span>
          </h1>
          <div className="mt-8 flex flex-wrap gap-4 lg:gap-6">
            <div className="bg-white/5 backdrop-blur border border-white/10 px-5 py-3">
              <div className="text-2xl font-serif text-white">{pins.projects?.length || 0}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#ffffff] mt-1">Projects</div>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 px-5 py-3">
              <div className="text-2xl font-serif text-white">{pins.listings?.length || 0}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#ffffff] mt-1">Live listings</div>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 px-5 py-3">
              <div className="text-2xl font-serif text-white">{pins.communities?.length || 0}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#ffffff] mt-1">Communities</div>
            </div>
            <div className="bg-[#ffffff] text-black px-5 py-3">
              <div className="text-2xl font-serif">{totalPins}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] mt-1">Pins on screen</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          {/* Filters — light card */}
          <aside className="border border-neutral-200 bg-white p-5 h-fit lg:sticky lg:top-24">
            <div className="text-xs tracking-[0.2em] uppercase text-[#001588] mb-3">Show on map</div>
            <div className="space-y-2 mb-6">
              {Object.entries({ projects: "Off-plan", listings: "Live listings", communities: "Communities" }).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer text-neutral-700 hover:text-[#001588]">
                  <input
                    type="checkbox"
                    data-testid={`map-toggle-${k}`}
                    checked={f.include[k]}
                    onChange={(e) => setF({ ...f, include: { ...f.include, [k]: e.target.checked } })}
                    className="accent-[#001588]"
                  />
                  <span
                    className="inline-block w-4 h-4 rounded-full flex-shrink-0 border border-white/40"
                    style={{ background: PIN_COLORS[k.slice(0, -1)] }}
                  />
                  {label}
                </label>
              ))}
            </div>

            <div className="text-xs tracking-[0.2em] uppercase text-[#001588] mb-3">Transit network</div>
            <div className="space-y-2 mb-6">
              {TRANSIT_LINES.map((line) => (
                <label key={line.id} className="flex items-center gap-2 text-sm cursor-pointer text-neutral-700 hover:text-[#001588]">
                  <input
                    type="checkbox"
                    data-testid={`map-transit-${line.id}`}
                    checked={transit[line.id]}
                    onChange={(e) => setTransit({ ...transit, [line.id]: e.target.checked })}
                    className="accent-[#001588]"
                  />
                  <span
                    className="inline-block w-6 h-[3px]"
                    style={{
                      background: `repeating-linear-gradient(90deg, ${line.color} 0 3px, transparent 3px 6px)`,
                    }}
                  />
                  {line.label}
                </label>
              ))}
            </div>

            <div className="text-xs tracking-[0.2em] uppercase text-[#001588] mb-3">Filters</div>
            <div className="space-y-3">
              <select className={inputCls} data-testid="map-filter-community" value={f.community} onChange={(e) => setF({ ...f, community: e.target.value })}>
                <option value="">All communities</option>
                {communities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select className={inputCls} value={f.developer_id} onChange={(e) => setF({ ...f, developer_id: e.target.value })}>
                <option value="">All developers</option>
                {developers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <select className={inputCls} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                <option value="">Any status</option>
                <option value="off-plan">Off-plan</option>
                <option value="under-construction">Under construction</option>
                <option value="ready">Ready</option>
                <option value="completed">Completed</option>
              </select>
              <select className={inputCls} value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })}>
                <option value="">Any property type</option>
                <option value="apartment">Apartment</option>
                <option value="villa">Villa</option>
                <option value="townhouse">Townhouse</option>
                <option value="penthouse">Penthouse</option>
              </select>
              <select className={inputCls} value={f.bedrooms_min} onChange={(e) => setF({ ...f, bedrooms_min: e.target.value })}>
                <option value="">Any bedrooms</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}+ bedrooms</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" placeholder="Price min" className={inputCls} value={f.price_min} onChange={(e) => setF({ ...f, price_min: e.target.value })} />
                <input type="number" placeholder="Price max" className={inputCls} value={f.price_max} onChange={(e) => setF({ ...f, price_max: e.target.value })} />
              </div>
              <button
                type="button"
                onClick={() => setF({ community: "", developer_id: "", status: "", price_min: "", price_max: "", property_type: "", bedrooms_min: "", include: f.include })}
                className="w-full text-xs uppercase tracking-[0.2em] py-2 border border-[#001588] text-[#001588] hover:bg-[#001588] hover:text-white transition-colors"
                data-testid="map-clear-filters"
              >
                Clear filters
              </button>
            </div>
          </aside>

          {/* Map — light-framed card */}
          <div className="border border-neutral-200 overflow-hidden shadow-lg relative" style={{ height: "76vh", minHeight: 560 }} data-testid="interactive-map-container">
            <MapContainer center={[25.15, 55.25]} zoom={11} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; OpenStreetMap contributors &copy; CARTO'
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              />
              <FitBounds points={allPoints} />

              {TRANSIT_LINES.map((line) => (
                <TransitLine key={line.id} line={line} show={transit[line.id]} />
              ))}

              {(f.include.projects ? pins.projects : []).filter((p) => p.latitude && p.longitude).map((p) => (
                <Marker key={`proj-${p.id}`} position={[p.latitude, p.longitude]} icon={makePinIcon(PIN_COLORS.project, "P")}>
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500">Project · {p.status}</div>
                      <div className="font-serif text-base mt-1">{p.name}</div>
                      <div className="text-xs text-gray-600">{p.community}</div>
                      {p.starting_price && <div className="text-sm mt-1">From {p.currency} {fmtCompact(p.starting_price)}</div>}
                      <Link to={`/projects/${p.slug}`} className="text-xs uppercase tracking-[0.2em] text-[#001588] hover:underline mt-2 inline-block">View project →</Link>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {(f.include.listings ? pins.listings : []).filter((p) => p.latitude && p.longitude).map((p) => (
                <Marker key={`list-${p.id}`} position={[p.latitude, p.longitude]} icon={makePinIcon(PIN_COLORS.listing, "L")}>
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500">Listing · {p.property_type}</div>
                      <div className="font-serif text-base mt-1">{p.title}</div>
                      <div className="text-xs text-gray-600">{p.community} · {p.bedrooms} bed</div>
                      {p.price && <div className="text-sm mt-1">{p.currency} {fmtCompact(p.price)}</div>}
                      <Link to={`/properties/${p.id}`} className="text-xs uppercase tracking-[0.2em] text-[#001588] hover:underline mt-2 inline-block">View listing →</Link>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {(f.include.communities ? pins.communities : []).filter((p) => p.latitude && p.longitude).map((p) => (
                <CircleMarker
                  key={`comm-${p.id}`} center={[p.latitude, p.longitude]}
                  radius={6} color={PIN_COLORS.community} fillColor={PIN_COLORS.community}
                  fillOpacity={0.85} weight={2} opacity={1}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500">Community</div>
                      <div className="font-serif text-base mt-1">{p.name}</div>
                      {p.average_price_apartment && <div className="text-xs text-gray-600">Avg apt: AED {p.average_price_apartment}/sqft</div>}
                      {p.rental_yield && <div className="text-xs text-gray-600">Yield: {p.rental_yield}%</div>}
                      <Link to={`/areas/${p.slug}`} className="text-xs uppercase tracking-[0.2em] text-[#001588] hover:underline mt-2 inline-block">Area guide →</Link>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
